/* eslint-disable max-lines */
import { isDefined } from "@clipboard-health/util-ts";
import { API_TYPES } from "@src/appV2/api";
import { isEmpty } from "lodash";
// FIXME: Automatically disabled during ESLint config update
// eslint-disable-next-line import/no-named-as-default
import z from "zod";

// Only these qualifications are currently supported for charting completion reports
const QualificationSchema = z.enum([
  "Certified Medication Aide",
  "CNA",
  "NURSE",
  "LVN",
  "RN",
  "CAREGIVER",
  "Medical Technician",
  "Respiratory Therapist",
]);
const ChartingNoncomplianceStateSchema = z.enum([
  "recalled",
  "review",
  "violation",
  "verified",
  "auto_verified",
  "reported_incomplete",
  "expired",
]);
const CompletionStateSchema = z.enum([
  "completed",
  "feedback",
  "recalled",
  "review",
  "violation",
  "verified",
  "auto_verified",
  "reported_incomplete",
  "expired",
]);

export type CompletionState = z.infer<typeof CompletionStateSchema>;

export type ValidChartingQualifications = z.infer<typeof QualificationSchema>;
export type ChartingNoncomplianceState = z.infer<typeof ChartingNoncomplianceStateSchema>;
export type QualificationOptions = "All Licenses" | ValidChartingQualifications;

const paginationLinksSchema = z.object({
  next: z.string().optional(),
  prev: z.string().optional(),
});

export const GetChartingCompletionReportsQuerySchema = z.object({
  filter: z.object({
    qualifications: z
      .string()
      .transform((value) => value.split(","))
      .refine((qualifications) =>
        qualifications
          .filter((qualification) => qualification !== "")
          .every((qualification) => QualificationSchema.parse(qualification))
      ),
    startDate: z.string().datetime(),
    endDate: z.string().datetime(),
    workerNameSearch: z.string().optional(),
  }),
  page: z
    .object({
      size: z.number().optional(),
      cursor: z.string().optional(),
    })
    .optional(),
});

const GetChartingCompletionReportShiftRelationshipSchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.shift),
    id: z.string(),
    attributes: z.object({
      startDate: z.string().datetime(),
      timeSlot: z.string(),
      qualification: QualificationSchema,
    }),
  }),
});

const GetChartingCompletionReportWorkerRelationshipSchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.worker),
    id: z.string(),
    attributes: z.object({
      name: z.string(),
    }),
  }),
});

const GetChartingCompletionReportShiftFeedbackRelationshipSchema = z
  .object({
    data: z.object({
      type: z.literal(API_TYPES.shiftFeedback),
      id: z.string(),
      attributes: z.object({
        completionPercentage: z.number().int().optional(),
        signedBy: z.string().optional(),
        completionStatus: z.enum(["not_started", "partially_completed", "completed"]).optional(),
      }),
    }),
  })
  .optional();

const GetChartingCompletionReportChartingNoncomplianceRelationshipSchema = z
  .object({
    data: z.object({
      type: z.literal(API_TYPES.chartingNoncompliance),
      id: z.string(),
      attributes: z.object({
        completionPercentage: z.number().int().optional(),
        chartingVerifiedAt: z.string().datetime().optional(),
        completionStatus: z.enum(["not_started", "partially_completed", "completed"]).optional(),
        incompleteChartingNotes: z.string().optional(),
        chartingCreatedBy: z.string().optional(),
        chartingCreatedByRole: z.string().optional(),
        chartingVerifiedBy: z.string().optional(),
        chartingVerifiedByRole: z.string().optional(),
        chartingDeadline: z.string().datetime().optional(),
        chartingReviewDeadline: z.string().datetime().optional(),
        chartingNoncomplianceState: ChartingNoncomplianceStateSchema.optional(),
        completionState: CompletionStateSchema.optional(),
        expiredAt: z.string().datetime().optional(),
        reportedIncompleteAt: z.string().datetime().optional(),
      }),
    }),
  })
  .optional();

const GetChartingCompletionReportRelationshipsSchema = z.object({
  shift: GetChartingCompletionReportShiftRelationshipSchema,
  worker: GetChartingCompletionReportWorkerRelationshipSchema,
  shiftFeedback: GetChartingCompletionReportShiftFeedbackRelationshipSchema,
  chartingNoncompliance: GetChartingCompletionReportChartingNoncomplianceRelationshipSchema,
});

const GetChartingCompletionReportDataSchema = z.object({
  type: z.literal(API_TYPES.chartingCompletionReport),
  id: z.string(),
  attributes: z.object({}),
  relationships: GetChartingCompletionReportRelationshipsSchema,
});

export type ChartingCompletionReportDataSchema = z.infer<
  typeof GetChartingCompletionReportDataSchema
>;

export const GetChartingCompletionReportsResponseSchema = z.object({
  data: z.array(GetChartingCompletionReportDataSchema),
  links: paginationLinksSchema.optional(),
});

export type GetChartingCompletionReportsResponse = z.infer<
  typeof GetChartingCompletionReportsResponseSchema
>;

const ChartingNoncomplianceDataTypeSchema = z.literal(API_TYPES.chartingNoncompliance);

const ChartingNoncomplianceShiftRelationshipSchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.shift),
    id: z.string(),
  }),
});

const ChartingNoncomplianceWorkplaceRelationshipSchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.workplace),
    id: z.string(),
  }),
});

const ChartingNoncomplianceWorkerRelationshipSchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.worker),
    id: z.string(),
  }),
});

const ChartingNoncomplianceCreatedByUserRelationshipSchema = z
  .object({
    data: z.object({
      type: z.enum([API_TYPES.adminUser, API_TYPES.workplaceUser]),
      id: z.string(),
    }),
  })
  .optional();

const ChartingNoncomplianceRelationshipsSchema = z.object({
  shift: ChartingNoncomplianceShiftRelationshipSchema,
  workplace: ChartingNoncomplianceWorkplaceRelationshipSchema,
  worker: ChartingNoncomplianceWorkerRelationshipSchema,
  createdByUser: ChartingNoncomplianceCreatedByUserRelationshipSchema,
});

const ChartingNoncomplianceAttributesSchema = z.object({
  modalContent: z.object({
    preDeactivationHeader: z.string(),
    preDeactivationSubtext: z.string(),
    deactivatedHeader: z.string(),
    deactivatedSubtext: z.string(),
  }),
  deadlineToChart: z.string().datetime(),
  chartingVerifiedAt: z.string().datetime().optional(),
  completionPercentage: z.number().int().min(0).max(99).optional(),
  state: ChartingNoncomplianceStateSchema.optional(),
});

const ChartingNoncomplianceDataSchema = z.object({
  type: ChartingNoncomplianceDataTypeSchema,
  id: z.string(),
  attributes: ChartingNoncomplianceAttributesSchema,
  relationships: ChartingNoncomplianceRelationshipsSchema,
});

const PostChartingNoncomplianceAttributesSchema = z.object({
  modalContent: z.object({
    preDeactivationHeader: z.string(),
    preDeactivationSubtext: z.string(),
    deactivatedHeader: z.string(),
    deactivatedSubtext: z.string(),
  }),
  hoursUntilDeadline: z.number().min(0),
  completionPercentage: z.number().int().min(0).max(99),
  incompleteChartingNotes: z.string().optional(),
  chartingStatus: z.enum(["not_started", "partially_completed"]),
  reportIncomplete: z.literal(true).optional(),
});

const PostChartingNoncomplianceRelationshipsSchema = z.object({
  shift: ChartingNoncomplianceShiftRelationshipSchema,
});

export const PostChartingNoncomplianceRequestSchema = z.object({
  data: z.object({
    type: ChartingNoncomplianceDataTypeSchema,
    attributes: PostChartingNoncomplianceAttributesSchema,
    relationships: PostChartingNoncomplianceRelationshipsSchema,
  }),
});

export const PostChartingNoncomplianceResponseSchema = z.object({
  data: ChartingNoncomplianceDataSchema,
});

export const PatchChartingNoncomplianceRequestSchema = z.object({
  data: z.object({
    type: ChartingNoncomplianceDataTypeSchema,
    id: z.string(),
    attributes: z.object({
      chartingVerifiedAt: z.string().datetime().optional(),
    }),
  }),
});

// FIXME: Automatically disabled during ESLint config update
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const GetChartingNoncompliancesQuerySchema = z
  .object({
    filter: z
      .object({
        chartingVerified: z
          .enum(["true", "false"])
          .transform((value: "true" | "false") => value === "true")
          .optional(),
        "workplace.id": z.string().optional(),
      })
      .optional(),
  })
  .optional()
  .default({ filter: {} });

const GetChartingNoncomplianceStateFilerSchema = z
  .string()
  .transform((value) => value.split(",").filter((state) => isDefined(state) && !isEmpty(state)))
  .refine(
    (states) => states.every((state) => ChartingNoncomplianceStateSchema.safeParse(state).success),
    { message: "Invalid state values" }
  )
  .transform((states) => states.map((state) => ChartingNoncomplianceStateSchema.parse(state)))
  .optional();

export const GetWorkplaceChartingNoncompliancesQuerySchema = z
  .object({
    filter: z
      .object({
        "worker.id": z.string().optional(),
        state: GetChartingNoncomplianceStateFilerSchema,
        createdByUserId: z.string().optional(),
      })
      .optional(),
  })
  .optional()
  .default({ filter: {} });

export const GetChartingNoncompliancesResponseSchema = z.object({
  data: z.array(ChartingNoncomplianceDataSchema),
});

export const PatchChartingNoncomplianceResponseSchema = z.object({
  data: ChartingNoncomplianceDataSchema,
});

export type PostChartingNoncomplianceRequest = z.infer<
  typeof PostChartingNoncomplianceRequestSchema
>;

export type PostChartingNoncomplianceResponse = z.infer<
  typeof PostChartingNoncomplianceResponseSchema
>;

export type PatchChartingNoncomplianceRequest = z.infer<
  typeof PatchChartingNoncomplianceRequestSchema
>;

export type PatchChartingNoncomplianceResponse = z.infer<
  typeof PatchChartingNoncomplianceResponseSchema
>;

export type GetChartingNoncompliancesQuery = z.infer<typeof GetChartingNoncompliancesQuerySchema>;

export type GetChartingNoncompliancesResponse = z.infer<
  typeof GetChartingNoncompliancesResponseSchema
>;

export type ChartingNoncomplianceData = z.infer<typeof ChartingNoncomplianceDataSchema>;
/* eslint-enable max-lines */
